import { Autocomplete, TextField } from "@mui/material";
import * as React from "react";

import {
  CollectionRecord,
  CollectionResourceType,
} from "@/shared/domain/collections";
import { useCollectionsQuery } from "@/shared/domain/collections/hooks/useCollectionsQuery";
import { OrgRecord } from "@/shared/domain/orgs";

import styles from "./CollectionSelector.module.css";

interface CollectionSelectorProps {
  collectionId: string | null;
  setCollectionId: (arg: string | null) => void;
  currentOrg: OrgRecord | null;
  /** When set, the picker only lists collections holding this resource type
   *  (e.g. ``event`` collections). Unset lists every collection. */
  contentType?: CollectionResourceType;
}

/**
 * Autocomplete over the caller's org collections. ``freeSolo`` so a
 * collection id can still be pasted/typed when it's outside the fetched
 * page. Mirrors {@link DatasetSelector}.
 */
export const CollectionSelector: React.FC<CollectionSelectorProps> = ({
  collectionId,
  setCollectionId,
  currentOrg,
  contentType,
}) => {
  const collectionsQuery = useCollectionsQuery({
    orgId: currentOrg?.org_id,
    resourceType: contentType,
  });

  const options: CollectionRecord[] = collectionsQuery.data?.items ?? [];

  return (
    <Autocomplete
      id="available-collections"
      options={options}
      fullWidth
      freeSolo
      value={collectionId}
      size={"small"}
      loading={collectionsQuery.isLoading}
      groupBy={() => "Recent Collections"}
      getOptionLabel={(option) =>
        (option as CollectionRecord).collection_id ?? option
      }
      renderOption={(props, option: CollectionRecord) => (
        <li className={styles.option} {...props}>
          <>
            <span className={styles.optionName}>
              {option.collection_id}
              {option.name ? ` — ${option.name}` : ""}
            </span>
            <span className={styles.optionMeta}>
              {option.created_by} on{" "}
              {new Date(option.created || "").toLocaleDateString()}
            </span>
          </>
        </li>
      )}
      onChange={(_event, newValue: CollectionRecord | string | null) => {
        if (typeof newValue === "object") {
          setCollectionId(newValue?.collection_id ?? null);
        } else {
          setCollectionId(newValue);
        }
      }}
      onInputChange={(_event, newInputValue, reason) => {
        // Ensures typed/pasted values are registered (see: ROBO-1370)
        if (reason === "input") {
          setCollectionId(newInputValue);
        }
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder="Collection" />
      )}
      componentsProps={{
        paper: {
          className: styles.paper,
        },
      }}
      className={styles.root}
    />
  );
};
